import { API_CONFIG, createApiUrl } from '../api';
import { getUserIdFromToken, isTokenExpired } from './jwt-utils';

export interface User {
  id: string;
  email: string;
  emailVerified?: boolean;
  firstName?: string;
  lastName?: string;
  phone?: string;
  phoneVerified?: boolean;
  status: string;
  roles: string[];
  createdAt: string;
};

export interface Session {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user?: User;
};

export interface AuthResponse {
  success: boolean;
  message?: string;
  data?: any;
};

export interface LoginRequest {
  email: string;
  password: string;
};

export interface RegisterRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  role?: string;
};

export interface RefreshRequest {
  refreshToken: string;
};

export interface LogoutRequest {
  refreshToken: string;
};

class ApiError extends Error {
  status: number;
  
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

type ApiErrorPayload = {
  message?: string;
  details?: string[] | string;
  hint?: string;
};

const formatApiErrorMessage = (status: number, rawBody: string): string => {
  if (!rawBody) {
    return `HTTP ${status}`;
  }

  try {
    const payload = JSON.parse(rawBody) as ApiErrorPayload;
    const details = Array.isArray(payload.details)
      ? payload.details.filter(Boolean).join(', ')
      : payload.details;

    if (payload.message === 'Request validation failed' && details) {
      return details;
    }

    if (payload.message && payload.hint) {
      return `${payload.message}. ${payload.hint}`;
    }

    if (payload.message) {
      return payload.message;
    }

    if (details) {
      return details;
    }
  } catch {
    // Response was not JSON; fall back to the raw body
  }

  return rawBody;
};

const CLIENT_ID_STORAGE_KEY = 'certi-client-id';
let inMemoryClientId: string | null = null;

const generateClientId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `client-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

export const getAuthClientId = (): string => {
  if (inMemoryClientId) {
    return inMemoryClientId;
  }

  try {
    const existingClientId = localStorage.getItem(CLIENT_ID_STORAGE_KEY);
    if (existingClientId) {
      inMemoryClientId = existingClientId;
      return existingClientId;
    }
  } catch {
    // localStorage can be unavailable in some browser contexts; fall back to in-memory storage
  }

  const newClientId = generateClientId();
  inMemoryClientId = newClientId;

  try {
    localStorage.setItem(CLIENT_ID_STORAGE_KEY, newClientId);
  } catch {
    // Best-effort persistence only; the in-memory fallback still keeps the current tab stable
  }

  return newClientId;
};

const makeRequest = async (url: string, options: RequestInit = {}): Promise<any> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      throw new ApiError(response.status, formatApiErrorMessage(response.status, errorText));
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }
    
    return null;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof ApiError) {
      throw error;
    }
    throw new Error(error instanceof Error ? error.message : 'Network error');
  }
};

const makeAuthRequest = async (url: string, options: RequestInit = {}): Promise<any> => {
  return makeRequest(url, {
    ...options,
    headers: {
      ...options.headers,
      'Client-Id': getAuthClientId(),
    },
  });
};

export const AuthAPI = {
  /**
   * Sign in with email and password
   */
  async signIn(email: string, password: string): Promise<Session> {
    const url = createApiUrl(API_CONFIG.ENDPOINTS.AUTH.LOGIN);
    const payload: LoginRequest = { email, password };
    
    const response = await makeAuthRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    return {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      expiresIn: response.expiresIn,
    };
  },

  /**
   * Create new user account
   */
  async signUp(
    firstName: string,
    lastName: string,
    email: string,
    password: string
  ): Promise<Session> {
    const url = createApiUrl(API_CONFIG.ENDPOINTS.AUTH.REGISTER);
    const payload: RegisterRequest = { 
      firstName, 
      lastName, 
      email, 
      password,
      role: 'BUYER' // Default role
    };
    
    const response = await makeAuthRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    return {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      expiresIn: response.expiresIn,
    };
  },

  /**
   * Send password reset email
   */
  async sendPasswordReset(email: string): Promise<void> {
    const url = createApiUrl(API_CONFIG.ENDPOINTS.AUTH.FORGOT_PASSWORD);

    await makeRequest(url, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  /**
   * Reset password with token
   */
  async resetPassword(token: string, password: string): Promise<void> {
    const url = createApiUrl(API_CONFIG.ENDPOINTS.AUTH.RESET_PASSWORD);

    await makeRequest(url, {
      method: 'POST',
      body: JSON.stringify({ token, newPassword: password }),
    });
  },

  /**
   * Sign out user
   */
  async signOut(refreshToken: string): Promise<void> {
    const url = createApiUrl(API_CONFIG.ENDPOINTS.AUTH.LOGOUT);
    const payload: LogoutRequest = { refreshToken };
    
    await makeAuthRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Refresh access token
   */
  async refreshToken(refreshToken: string): Promise<Session> {
    const url = createApiUrl(API_CONFIG.ENDPOINTS.AUTH.REFRESH);
    const payload: RefreshRequest = { refreshToken };
    
    const response = await makeAuthRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    return {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      expiresIn: response.expiresIn,
    };
  },

  /**
   * Verify email with token
   */
  async verifyEmail(token: string): Promise<void> {
    const url = createApiUrl('/api/auth/verify-email');

    await makeRequest(url, {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  },

  /**
   * Resend email verification
   */
  async resendVerificationEmail(email: string): Promise<void> {
    const url = createApiUrl('/api/auth/resend-verification');

    await makeRequest(url, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  /**
   * Get current user profile
   */
  async getCurrentUser(accessToken: string): Promise<User> {
    const userId = getUserIdFromToken(accessToken);
    if (!userId) {
      throw new Error('Invalid access token');
    }

    const url = createApiUrl(API_CONFIG.ENDPOINTS.USER.PROFILE.replace('{userId}', userId));
    
    const response = await makeRequest(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    return response;
  },

  /**
   * Check if access token needs refresh
   */
  isTokenExpired(accessToken: string): boolean {
    return isTokenExpired(accessToken);
  },

  /**
   * Google OAuth sign in
   */
  async signInWithGoogle(idToken: string): Promise<Session> {
    const url = createApiUrl('/api/auth/google');

    const response = await makeAuthRequest(url, {
      method: 'POST',
      body: JSON.stringify({ credential: idToken }),
    });

    return {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      expiresIn: response.expiresIn,
    };
  },

  /**
   * Apple OAuth sign in
   */
  async signInWithApple(
    identityToken: string,
    user?: { name?: { firstName?: string; lastName?: string } }
  ): Promise<Session> {
    const url = createApiUrl('/api/auth/apple');

    const payload: any = { identityToken };
    if (user?.name?.firstName) payload.firstName = user.name.firstName;
    if (user?.name?.lastName) payload.lastName = user.name.lastName;

    const response = await makeAuthRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    return {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      expiresIn: response.expiresIn,
    };
  },
};
